import { SubjectRoles } from "@/api-schema-enums";
import { getFirstNavigationUrl } from "@/common/helpers/navigation";
import type { NavigationItem, RouteAudience } from "@/config/navigation/types";

export type RouteEntry = {
  path: string;
  component: NonNullable<NavigationItem["component"]>;
  nest?: boolean;
};

export type RouteRedirectEntry = {
  path: string;
  target: string;
};

export function collectRouteEntries(
  items: NavigationItem[],
  isInternalUser: boolean,
  result: RouteEntry[] = [],
  shouldSort = true,
): RouteEntry[] {
  for (const item of items) {
    if (isRouteAudienceMatch(item.audience, isInternalUser) && item.routePath && item.component) {
      result.push({ path: item.routePath, component: item.component, nest: item.nest });
    }

    if (item.children) {
      collectRouteEntries(item.children, isInternalUser, result, false);
    }
  }

  return shouldSort ? sortRouteEntries(result) : result;
}

export function filterRouteItemsByAudience(items: NavigationItem[], isInternalUser: boolean): NavigationItem[] {
  return items.reduce((acc, item) => {
    if (!isRouteAudienceMatch(item.audience, isInternalUser)) {
      return acc;
    }

    if (item.children) {
      const filteredChildren = filterRouteItemsByAudience(item.children, isInternalUser);
      acc.push({ ...item, children: filteredChildren });
      return acc;
    }

    acc.push(item);
    return acc;
  }, [] as NavigationItem[]);
}

export function collectModuleRouteEntries(moduleItem?: NavigationItem): RouteEntry[] {
  if (!moduleItem?.routePath || !moduleItem.children) return [];
  const entries: RouteEntry[] = [];

  for (const child of moduleItem.children) {
    appendRouteEntries(child, moduleItem.routePath, entries);
  }

  return sortRouteEntries(entries);
}

export function collectModuleRedirectEntries(moduleItem?: NavigationItem): RouteRedirectEntry[] {
  if (!moduleItem?.routePath || !moduleItem.children) return [];
  const entries: RouteRedirectEntry[] = [];

  for (const child of moduleItem.children) {
    appendRedirectEntries(child, moduleItem.routePath, entries);
  }

  return entries;
}

export function getFirstDescendantNavigationUrl(item?: NavigationItem): string | undefined {
  if (!item?.children) return undefined;

  for (const child of item.children) {
    const url = getFirstNavigationUrl(child);
    if (url) return url;
  }

  return undefined;
}

/**
 * Returns the top-level navigation item (module) whose routePath is a prefix of
 * the given routePath. Only searches the flat top-level array — no recursion.
 */
export function findTopLevelNavigationItemByPath(
  items: NavigationItem[],
  routePath: string,
): NavigationItem | undefined {
  let bestMatch: NavigationItem | undefined;

  for (const item of items) {
    if (!item.routePath || item.routePath === "/") continue;

    if (isMatchingRoutePath(item.routePath, routePath)) {
      if (!bestMatch || item.routePath.length > bestMatch.routePath!.length) {
        bestMatch = item;
      }
    }
  }

  return bestMatch;
}

export function findBestMatchingNavigationItemByPath(
  items: NavigationItem[],
  routePath: string,
): NavigationItem | undefined {
  let bestMatch: NavigationItem | undefined;

  const stack: NavigationItem[] = [...items];

  for (let index = 0; index < stack.length; index++) {
    const item = stack[index];

    if (item.routePath && isMatchingRoutePath(item.routePath, routePath)) {
      if (!bestMatch || item.routePath.length > bestMatch.routePath!.length) {
        bestMatch = item;
      }
    }

    if (item.children?.length) {
      stack.push(...item.children);
    }
  }

  return bestMatch;
}

function isRouteAudienceMatch(audience: RouteAudience | undefined, isInternalUser: boolean): boolean {
  if (!audience) return true;

  const userAudience = isInternalUser ? SubjectRoles.Int : SubjectRoles.Ext;

  if (Array.isArray(audience)) {
    return audience.includes(userAudience);
  }

  return audience === userAudience;
}

function appendRouteEntries(item: NavigationItem, rootPath: string, entries: RouteEntry[]) {
  if (item.routePath && item.component) {
    entries.push({
      path: toRelativePath(rootPath, item.routePath),
      component: item.component,
      nest: item.nest,
    });
  }

  if (item.children) {
    for (const child of item.children) {
      appendRouteEntries(child, rootPath, entries);
    }
  }
}

function appendRedirectEntries(item: NavigationItem, rootPath: string, entries: RouteRedirectEntry[]) {
  if (item.routePath && item.children?.length && !item.component) {
    const target = getFirstDescendantNavigationUrl(item);
    if (target) {
      entries.push({ path: toRelativePath(rootPath, item.routePath), target });
    }
  }

  if (item.children) {
    for (const child of item.children) {
      appendRedirectEntries(child, rootPath, entries);
    }
  }
}

function toRelativePath(rootPath: string, path: string): string {
  if (path === rootPath) return "/";
  if (!path.startsWith(rootPath)) return path;
  const relative = path.slice(rootPath.length);
  return relative.startsWith("/") ? relative : `/${relative}`;
}

export function isMatchingRoutePath(candidatePath: string, routePath: string): boolean {
  return routePath === candidatePath || routePath.startsWith(`${candidatePath}/`);
}

function sortRouteEntries(entries: RouteEntry[]): RouteEntry[] {
  return entries.sort((a, b) => b.path.length - a.path.length);
}
